import { useState, useCallback, useEffect } from 'react'
import {
  getConversations,
  createConversation as createConversationApi,
  deleteConversation as deleteConversationApi,
  getConversationMessages,
} from 'services/ai_agent'

// ============================================================
// Types
// ============================================================

export interface Conversation {
  id: number
  chat_id: string
  title: string
  created_at: string
  last_message_at: string
}

export interface ConversationMessage {
  id: string
  role: 'user' | 'assistant' | 'tool'
  content: string
  created_at: string
}

// ============================================================
// Hook
// ============================================================

export function useConversations() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)

  // ----------------------------------------------------------
  // Fetch conversation list
  // ----------------------------------------------------------
  const fetchConversations = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await getConversations()
      const data = response.data
      // Normalize: API may return { results: [...] } or [...]
      const list = Array.isArray(data) ? data : data.results ?? []
      setConversations(list)
    } catch (err) {
      console.error('[useConversations] Failed to fetch conversations:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // ----------------------------------------------------------
  // Create new conversation
  // ----------------------------------------------------------
  const createConversation = useCallback(async (title?: string): Promise<Conversation | null> => {
    try {
      const response = await createConversationApi(title || 'Nuova chat')
      const newConv: Conversation = response.data
      setConversations((prev) => [newConv, ...prev])
      return newConv
    } catch (err) {
      console.error('[useConversations] Failed to create conversation:', err)
      return null
    }
  }, [])

  // ----------------------------------------------------------
  // Delete conversation
  // ----------------------------------------------------------
  const deleteConversation = useCallback(async (chatId: string) => {
    try {
      await deleteConversationApi(chatId)
      setConversations((prev) => prev.filter((c) => c.chat_id !== chatId))
      // If the deleted conversation was active, deselect it
      setActiveConversationId((prev) => (prev === chatId ? null : prev))
      return true
    } catch (err) {
      console.error('[useConversations] Failed to delete conversation:', err)
      return false
    }
  }, [])

  // ----------------------------------------------------------
  // Load messages for a conversation
  // ----------------------------------------------------------
  const loadMessages = useCallback(async (chatId: string): Promise<ConversationMessage[]> => {
    setIsLoadingMessages(true)
    try {
      const response = await getConversationMessages(chatId)
      const data = response.data
      const msgs = Array.isArray(data) ? data : data.results ?? []
      return msgs
    } catch (err) {
      console.error('[useConversations] Failed to load messages:', err)
      return []
    } finally {
      setIsLoadingMessages(false)
    }
  }, [])

  // ----------------------------------------------------------
  // Select conversation
  // ----------------------------------------------------------
  const selectConversation = useCallback((chatId: string | null) => {
    setActiveConversationId(chatId)
  }, [])

  // ----------------------------------------------------------
  // Derived state
  // ----------------------------------------------------------
  const activeConversation = conversations.find((c) => c.chat_id === activeConversationId) ?? null

  // Load conversations on mount
  useEffect(() => {
    fetchConversations()
  }, [fetchConversations])

  return {
    conversations,
    activeConversation,
    activeConversationId,
    isLoading,
    isLoadingMessages,
    fetchConversations,
    createConversation,
    deleteConversation,
    loadMessages,
    selectConversation,
  }
}
